import { ParsedRowError, ProjectRow } from "../../../../../models";
import { BulkCreateProjectsRequestProject, CreateProjectRequest } from "../../../../../service/project";
import { ServiceError } from "../../../../../service/Shared";
import { LANDHOLDING_ERROR_SCOPE, resolveCreateProjectErrorMessage, trailingPropertyName } from "./createProjectErrors";

// row.key is the uuid the api persists (BR-02888) - sending it makes the Step-2 save retry-safe,
// because a resubmit hits the project_uuid unique key instead of creating a second project.
export const mapManualRowToCreateProjectRequest = (
  row: ProjectRow,
  developerUuid: string | null
): Omit<CreateProjectRequest, "validateOnly"> => ({
  developerUuid,
  displayName: row.displayName,
  projectTypeUuid: row.projectTypeUuid,
  standardUuid: row.standardUuid,
  uuid: row.key,
  vvbUuid: row.vvbUuid,
  addressCountryCode: row.addressCountryCode || null,
  addressPostcode: row.addressPostcode || null,
  addressRegion: row.addressRegion || null,
  areaNetHa: row.areaNetHa ?? null,
  locationCoordinates: row.latitude || row.longitude ? `${row.latitude || ""},${row.longitude || ""}` : null,
});

// Takes the same ProjectRow a typed entry uses - a parsed row is the same shape, and the api cannot
// tell a typed row from a parsed one.
export const mapRowToBulkProject = (
  row: ProjectRow,
  developerUuid: string | null
): BulkCreateProjectsRequestProject => ({
  ...mapManualRowToCreateProjectRequest(row, developerUuid),
  uuid: row.key,
  vvbUuid: row.vvbUuid,
});

// The api stamps a bulk error with the row's own uuid when it can (BR-02888); when it cannot - the
// row supplied none, or an older api build - it still names the row by its position in the request,
// as "Projects[i].Property". Either is enough to resolve the error back to the row it came from.
const rowIndexPattern = /Projects\[(\d+)\]/i;

const resolveRowIndex = (error: ServiceError, rows: ProjectRow[]): number => {
  const uuid = error.parameters?.uuid;
  if (uuid) {
    const uuidIndex = rows.findIndex((row) => row.key === uuid);
    if (uuidIndex !== -1) return uuidIndex;
  }

  const match = rowIndexPattern.exec(error.path ?? error.property ?? "");
  const pathIndex = match ? Number(match[1]) : -1;
  return pathIndex >= 0 && pathIndex < rows.length ? pathIndex : -1;
};

// The uuid fields the parser resolves from a name in the file, with the name it kept beside each.
const RESOLVED_FROM_NAME: Record<string, { label: string; name: (row: ProjectRow) => string | undefined }> = {
  StandardUuid: { label: "standard", name: (row) => row.standardName },
  ProjectTypeUuid: { label: "project type", name: (row) => row.projectTypeName },
};

// A name the parser could not match reaches the api as a missing uuid, so the api calls it required.
// The file did have a value there - it was the wrong one.
const describeUnresolvedName = (error: ServiceError, row: ProjectRow): string | null => {
  if (error.code !== "VALIDATION_ATTRIBUTE_REQUIRED" || !error.property) return null;

  const field = RESOLVED_FROM_NAME[trailingPropertyName(error.property)];
  const name = field?.name(row);
  return name ? `"${name}" is not a valid ${field.label}` : null;
};

// An uploaded landholding's own error - its name, its reference, a reference already taken - is
// stamped onto one of its rows by callProjectDetails with this scope, so that the views name the
// landholding rather than the project that happens to carry it.
const isLandholdingError = (error: ServiceError): boolean => error.parameters?.scope === LANDHOLDING_ERROR_SCOPE;

// A failing row resolves back to the parsed row it came from. Errors that belong to no row
// (payload-level failures) resolve to no index and are left for the form-level error box.
export const mapBulkErrorToParsedRowError = (error: ServiceError, rows: ProjectRow[]): ParsedRowError | null => {
  const index = resolveRowIndex(error, rows);
  if (index === -1) return null;

  const row = rows[index];

  const reason = describeUnresolvedName(error, row) ?? resolveCreateProjectErrorMessage(error);

  // A parsed row always carries its spreadsheet row; the fallback only guards the optional type.
  const rowNumber = row.rowNumber ?? index + 1;

  // The landholding's own failure is named as the landholding's - the row it was stamped onto is
  // only its address, and the project on that row is not what failed.
  const label = isLandholdingError(error)
    ? `Landholding ${row.landholdingName || "Unnamed landholding"}`
    : row.displayName || `Row ${rowNumber}`;

  return { rowNumber, label, reason };
};

/*
  The manual forms show a failure against the field it belongs to, so the api's errors are grouped by
  the row they resolve to rather than flattened into one list. No project-name prefix here: the
  error is already sitting inside that project's own form.

  Errors that resolve to no row - a payload-level failure, or the landholding itself - are left for
  the form-level box.
*/
export const groupBulkErrorsByRowKey = (
  errors: ServiceError[] | undefined,
  rows: ProjectRow[]
): Record<string, ServiceError[]> => {
  const byKey: Record<string, ServiceError[]> = {};

  (errors ?? []).forEach((error) => {
    // A typed landholding's own errors go to its form (getLandholdingErrors), never to a row.
    if (isLandholdingError(error)) return;

    const index = resolveRowIndex(error, rows);
    if (index === -1) return;

    const { key } = rows[index];
    const message = resolveCreateProjectErrorMessage(error);
    byKey[key] = [...(byKey[key] ?? []), { ...error, message }];
  });

  return byKey;
};

// Labels each error with the project it came from. Errors that resolve to no row keep their own
// message.
export const labelBulkErrorsByRow = (errors: ServiceError[] | undefined, rows: ProjectRow[]): ServiceError[] =>
  (errors ?? []).map((error) => {
    const index = resolveRowIndex(error, rows);
    const message = resolveCreateProjectErrorMessage(error);

    if (index === -1) return { ...error, message };

    if (isLandholdingError(error)) {
      const landholdingName = rows[index].landholdingName || "Unnamed landholding";
      return { ...error, message: `Landholding ${landholdingName}: ${message}` };
    }

    return { ...error, message: `${rows[index].displayName || `Project ${index + 1}`}: ${message}` };
  });
